import * as contracts from "../contracts/contracts";
import Activity from "./activity";
import ActivityEntity from "./activityEntity";
import WindowType from "./windowType";
import { ifNotUndefined } from "../helpers/util";
import { WindowDefinition } from "./windowDefinition";

import { Glue42 } from "../../../glue";
type OverrideTypeDefinition = Glue42.Activities.OverrideTypeDefinition;

/**
 * An activity type is a definition template for an activity, consisting of a collection of window types,
 * their layout and an initial activity context.
 *
 * @module activityType
 */
export default class ActivityType extends ActivityEntity {

  public _name: string;
  public _description: string;
  public _ownerWindow: WindowType;
  public _helperWindows: WindowType[];

  constructor(name: string, ownerWindow: WindowType, helperWindows?: WindowType[], description?: string) {
    super(name);
    this._name = name;
    this._description = description;
    this._ownerWindow = ownerWindow;
    this._helperWindows = helperWindows || [];
  }

  /**
   * @var {string} name Name of the activity type
   */
  public get name(): string {
    return this._name;
  }

  /**
   * @var {string} description description for the activity type
   */
  public get description(): string {
    return this._description;
  }

  /**
   * A list of window types that should be created when initiating a new instance of that activity type
   * @var {windowDefinition[]} helperWindows
   */
  public get helperWindows(): WindowType[] {
    return this._helperWindows.map((hw) => this.covertToWindowDef(hw));
  }

  /**
   * @var {windowDefinition} ownerWindow Returns the definition of the owner window for that activity type
   */
  public get ownerWindow(): WindowType {
    return this.covertToWindowDef(this._ownerWindow);
  }

  /**
   * Initiates a new activity of this type
   *
   * @function initiate
   * @param {object} context The initial context to be used for the new activity
   * @param callback
   * @param configuration
   * @returns {Promise<activity>}
   */
  public initiate(context: object, callback?: contracts.Callback<Activity>, configuration?: WindowDefinition[] | OverrideTypeDefinition): Promise<Activity> {
    return this._manager.initiate(this._name, context, callback, configuration);
  }

  public _updateCore(other: ActivityType) {
    super._updateCore(other);
    ifNotUndefined(other._description, (x) => this._description = x);
    ifNotUndefined(other._ownerWindow, (x) => this._ownerWindow = x);
    ifNotUndefined(other._helperWindows, (x) => this._helperWindows = x);
  }

  private covertToWindowDef(windowType: any): any {
    // GD2 + old glue js backward compatible
    return {
      type: windowType?.id?.type,
      name: windowType?.id?.name
    };
  }
}
